import { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useTermsAccepted } from '@/hooks/useTermsAccepted';

const COOKIE_CONSENT_KEY = 'tempvault_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'tempvault_cookie_preferences';

type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'custom';

export interface CookiePreferences {
  essential: boolean; // Always true
  functional: boolean;
  analytics: boolean;
  advertising: boolean;
}

const defaultPreferences: CookiePreferences = {
  essential: true,
  functional: true,
  analytics: true,
  advertising: true,
};

export const getCookiePreferences = (): CookiePreferences => {
  try {
    const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (stored) {
      return { ...defaultPreferences, ...JSON.parse(stored), essential: true };
    }
  } catch {
    // Ignore parse errors
  }
  return defaultPreferences;
};

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<ConsentStatus>('pending');

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored === 'accepted' || stored === 'rejected' || stored === 'custom') {
      setConsent(stored);
    }
  }, []);

  return consent;
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const termsAccepted = useTermsAccepted();

  useEffect(() => {
    if (termsAccepted) {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) {
        const timer = setTimeout(() => setVisible(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [termsAccepted]);

  // Listen for custom event to reopen cookie settings
  useEffect(() => {
    const handleOpenCookieSettings = () => {
      // Load current preferences when reopening
      setPreferences(getCookiePreferences());
      setVisible(true);
    };

    window.addEventListener('openCookieSettings', handleOpenCookieSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
  }, []);

  const savePreferences = (prefs: CookiePreferences, status: ConsentStatus) => {
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem(COOKIE_CONSENT_KEY, status);
    setVisible(false);
    window.location.reload();
  };

  const handleAcceptAll = () => {
    savePreferences(defaultPreferences, 'accepted');
  };

  const handleRejectNonEssential = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      advertising: false,
    };
    savePreferences(essentialOnly, 'rejected');
  };

  const handleSavePreferences = () => {
    savePreferences(preferences, 'custom');
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Essential cannot be disabled
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
      <div className="max-w-4xl mx-auto glass-premium border border-primary/20 rounded-2xl p-6 shadow-glow bg-background/95 backdrop-blur-xl">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center">
              <Cookie className="w-6 h-6 text-primary-foreground" />
            </div>
          </div>
          
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Cookie Preferences</h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={handleRejectNonEssential}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground leading-relaxed">
              We use cookies to enhance your browsing experience and display personalized advertisements. 
              Customize your preferences below or accept all cookies.
            </p>

            {showDetails && (
              <div className="space-y-3 pt-3 border-t border-border/50 animate-in fade-in duration-200">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-success mt-1.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Essential Cookies</p>
                      <p className="text-xs text-muted-foreground">Required for basic site functionality. Cannot be disabled.</p>
                    </div>
                  </div>
                  <Switch checked={true} disabled className="opacity-50" />
                </div>

                {/* Functional Cookies */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary-foreground mt-1.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Functional Cookies</p>
                      <p className="text-xs text-muted-foreground">Remember your preferences, theme settings, and session data.</p>
                    </div>
                  </div>
                  <Switch 
                    checked={preferences.functional} 
                    onCheckedChange={() => togglePreference('functional')}
                  />
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-accent mt-1.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Analytics Cookies</p>
                      <p className="text-xs text-muted-foreground">Help us understand how visitors interact with our website to improve it.</p>
                    </div>
                  </div>
                  <Switch 
                    checked={preferences.analytics} 
                    onCheckedChange={() => togglePreference('analytics')}
                  />
                </div>

                {/* Advertising Cookies */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Advertising Cookies</p>
                      <p className="text-xs text-muted-foreground">Used by Google AdSense to show personalized ads based on your interests.</p>
                    </div>
                  </div>
                  <Switch 
                    checked={preferences.advertising} 
                    onCheckedChange={() => togglePreference('advertising')}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                onClick={handleAcceptAll}
                className="gradient-bg text-primary-foreground shadow-glow hover:shadow-glow-intense transition-all"
              >
                <Check className="w-4 h-4 mr-2" />
                Accept All
              </Button>
              {showDetails ? (
                <Button
                  variant="secondary"
                  onClick={handleSavePreferences}
                  className="border-primary/30"
                >
                  Save Preferences
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={handleRejectNonEssential}
                  className="border-border/50 hover:bg-secondary/50"
                >
                  Reject Non-Essential
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => setShowDetails(!showDetails)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Settings className="w-4 h-4 mr-2" />
                {showDetails ? 'Hide Details' : 'Customize'}
                {showDetails ? (
                  <ChevronUp className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
